import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { scrollToId } from "../hooks/useLenis";

const LINKS = [
  { label: "Story", href: "#story" },
  { label: "Family", href: "#family" },
  { label: "Episodes", href: "#episodes" },
  { label: "Lessons", href: "#lessons" },
  { label: "Watch", href: "#watch" },
  { label: "About", href: "#creator" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    scrollToId(href);
  };

  return (
    <>
      <motion.header
        initial={{ y: -70, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 1, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-700 ${
          scrolled ? "bg-ink/70 backdrop-blur-xl border-b border-white/10" : "bg-transparent border-b border-transparent"
        }`}
        data-testid="main-navbar"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 md:h-20 flex items-center justify-between">
          <button
            onClick={() => window.__lenis?.scrollTo(0, { duration: 1.4 })}
            className="flex items-center gap-3 font-display text-sm md:text-base tracking-[0.3em] text-ivory hover:text-gold-light transition-colors duration-300"
            data-testid="nav-logo"
          >
            <img
              src="/assets/img/logo.png"
              alt="WE NEED TO TALK"
              className="h-9 w-9 md:h-10 md:w-10 rounded-full object-cover ring-1 ring-gold/40"
              data-testid="nav-logo-image"
            />
            <span className="hidden sm:inline">
              WE NEED <span className="italic text-gold-light">TO TALK</span>
            </span>
          </button>
          <nav className="hidden md:flex items-center gap-8">
            {LINKS.map((l) => (
              <button
                key={l.href}
                onClick={() => go(l.href)}
                className="text-[11px] tracking-[0.28em] uppercase text-smoke hover:text-gold-light transition-colors duration-300"
                data-testid={`nav-link-${l.label.toLowerCase()}`}
              >
                {l.label}
              </button>
            ))}
          </nav>
          <button
            className="md:hidden text-ivory"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
            data-testid="nav-mobile-toggle"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-40 bg-ink/95 backdrop-blur-xl flex flex-col items-center justify-center gap-8 md:hidden"
            data-testid="nav-mobile-menu"
          >
            {LINKS.map((l, i) => (
              <motion.button
                key={l.href}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.08 * i, duration: 0.5 }}
                onClick={() => go(l.href)}
                className="font-display text-3xl text-ivory hover:text-gold-light transition-colors duration-300"
                data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
              >
                {l.label}
              </motion.button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
